"use client";

import Link from "next/link";
import { ArrowDown } from "lucide-react";

import { Container } from "@/components/primitives/Container";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Button } from "@/components/primitives/Button";
import { Reveal } from "@/components/motion/Reveal";
import { getPartners } from "@/content/partners";
import { useLocale } from "@/lib/i18n/provider";

export function PartnersHero() {
  const { locale } = useLocale();
  const copy = getPartners(locale);

  return (
    <section className="relative overflow-hidden pt-36 pb-20 md:pt-48 md:pb-28">
      {/* Ambient accent glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -z-10 h-[700px] w-[700px] -translate-x-1/2 rounded-full blur-[160px]"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, rgba(var(--glow-accent-rgb), 0.24) 0%, rgba(var(--glow-accent-rgb), 0) 70%)",
        }}
      />

      <Container>
        <div className="max-w-4xl">
          <Reveal delay={0}>
            <Eyebrow>{copy.hero.eyebrow}</Eyebrow>
          </Reveal>

          <Reveal delay={0.07}>
            <h1 className="mt-5 whitespace-pre-line text-[clamp(3rem,7vw,5.5rem)] font-bold leading-[1.00] tracking-[-0.038em] text-[var(--color-text-primary)]">
              {copy.hero.title}
            </h1>
          </Reveal>

          <Reveal delay={0.14}>
            <p className="mt-6 text-[clamp(1.15rem,1.8vw,1.4rem)] font-medium leading-snug tracking-tight text-[var(--color-accent-base)]">
              {copy.hero.subtitle}
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="mt-5 max-w-[68ch] text-[clamp(1rem,1.2vw,1.125rem)] leading-relaxed text-[var(--color-text-secondary)]">
              {copy.hero.body}
            </p>
          </Reveal>

          <Reveal delay={0.27}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild variant="primary" size="lg">
                <Link href="#apply">
                  {copy.hero.ctaLabel}
                </Link>
              </Button>
              <Link
                href="#tiers"
                aria-label={copy.hero.secondaryCtaAriaLabel}
                className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
              >
                {copy.hero.secondaryCtaLabel}
                <ArrowDown size={14} aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

export default PartnersHero;
